import { nextTick, watch, onMounted, onBeforeUnmount, type Ref } from 'vue';

interface UseTextareaAutoGrowOptions {
    textarea: Ref<HTMLTextAreaElement | null>;
    text: Ref<string>;
    maxRows?: number;
    onBeforeResize?: () => void;
    onResize?: () => void;
}

export const useTextareaAutoGrow = (options: UseTextareaAutoGrowOptions) => {
    const { textarea, text, maxRows = 6, onBeforeResize, onResize } = options;

    let lastHeight = 0;
    let lastObservedWidth = 0;
    let cachedLineHeight = 0;
    let cachedVerticalPadding = 0;
    let cachedVerticalBorder = 0;
    let resizeObserver: ResizeObserver | null = null;
    let resizePending = false;

    const measureMetrics = (el: HTMLTextAreaElement): void => {
        const style = globalThis.getComputedStyle(el);
        cachedVerticalPadding =
            (Number.parseFloat(style.paddingTop) || 0) +
            (Number.parseFloat(style.paddingBottom) || 0);
        cachedVerticalBorder =
            (Number.parseFloat(style.borderTopWidth) || 0) +
            (Number.parseFloat(style.borderBottomWidth) || 0);

        // Measure one visual row empirically by collapsing the textarea to a
        // single row with auto height. offsetHeight includes padding + border
        // (border-box), so subtracting them yields the pure per-line height.
        // This avoids relying on computed lineHeight, which can be "normal".
        el.rows = 1;
        el.style.height = 'auto';
        const singleRowHeight = el.offsetHeight;
        cachedLineHeight = singleRowHeight - cachedVerticalPadding - cachedVerticalBorder;

        if (cachedLineHeight <= 0) {
            cachedLineHeight = 0;
        }
    };

    const resize = async (): Promise<void> => {
        if (resizePending) {
            return;
        }

        resizePending = true;
        await nextTick();
        resizePending = false;

        const el = textarea.value;

        if (!el) {
            return;
        }

        if (cachedLineHeight <= 0) {
            measureMetrics(el);
        }

        if (cachedLineHeight <= 0) {
            return;
        }

        const maxHeight = cachedLineHeight * maxRows + cachedVerticalPadding + cachedVerticalBorder;

        // scrollHeight = padding + content (no border); add border to match
        // the border-box height we are about to assign.
        el.rows = 1;
        el.style.height = 'auto';
        const contentHeight = el.scrollHeight + cachedVerticalBorder;

        const newHeight = Math.min(contentHeight, maxHeight);

        if (newHeight !== lastHeight) {
            onBeforeResize?.();
        }

        el.style.height = `${newHeight}px`;

        if (newHeight !== lastHeight) {
            lastHeight = newHeight;
            onResize?.();
        }
    };

    const onWidthChange = (): void => {
        cachedLineHeight = 0;
        resize();
    };

    watch(text, () => {
        resize();
    });

    onMounted(() => {
        if (textarea.value && typeof ResizeObserver !== 'undefined') {
            lastObservedWidth = textarea.value.getBoundingClientRect().width;
            resizeObserver = new ResizeObserver((entries) => {
                const newWidth = entries[0]?.contentRect.width ?? 0;
                if (newWidth !== lastObservedWidth) {
                    lastObservedWidth = newWidth;
                    onWidthChange();
                }
            });
            resizeObserver.observe(textarea.value);
        }
    });

    onBeforeUnmount(() => {
        resizeObserver?.disconnect();
    });
};
